import { useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import { SquareButton } from '@app/core';
import { Checkbox } from '@app/form';

import { FilterPopover } from '../filters/FilterPopover';

// @TODO Move in components folder
import { FieldConfig } from 'pages/Dashboard/Sessions/Details/hooks/useFieldConfig';

interface Props {
  fieldConfigs: FieldConfig[];
  hiddenColumns: string[];
  onHiddenColumnsChange: (v: string[]) => any;
  onClear?: () => string[];
}

const useStyles = createUseStyles((theme: Theme) => ({
  choiceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase / 2,
  },
}));

export const ColumnVisibilitySelect = ({
  fieldConfigs,
  hiddenColumns: hiddenColumnsProps,
  onHiddenColumnsChange,
  onClear,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | undefined>(
    undefined,
  );
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const onApply = () => {
    onHiddenColumnsChange(hiddenColumns);
    setAnchorEl(undefined);
  };

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setHiddenColumns(hiddenColumnsProps);
  };

  const onToggle = (checked: boolean, config: FieldConfig) => {
    if (!checked) {
      setHiddenColumns([...hiddenColumns, config.field]);
    } else {
      setHiddenColumns(hiddenColumns.filter((c) => c !== config.field));
    }
  };

  const handleClear = () => {
    if (!onClear) {
      return;
    }
    const newVal = onClear();
    setHiddenColumns(newVal);
    setAnchorEl(undefined);
  };

  return (
    <>
      <SquareButton
        icon="ri-settings-3-line"
        color="ink"
        onClick={onClick}
        type="button"
        title={t('sessions.details.participationsDetails.visibleColumnsToggle')}
      />
      <FilterPopover
        anchorEl={anchorEl}
        onApply={onApply}
        onClear={onClear ? handleClear : undefined}
        onRequestClose={() => setAnchorEl(undefined)}
        title={t('sessions.details.participationsDetails.visibleColumns')}
      >
        <div className={classes.choiceList}>
          {fieldConfigs.map(
            (config) =>
              config.relevant && (
                <Checkbox
                  noMargin
                  checked={!hiddenColumns.includes(config.field)}
                  labelColorChange
                  onChange={(e) => onToggle(e.currentTarget.checked, config)}
                >
                  <span>{config.label}</span>
                </Checkbox>
              ),
          )}
        </div>
      </FilterPopover>
    </>
  );
};
